import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, FileText, Wrench, PoundSterling, Settings as SettingsIcon,
  ScrollText, Eye, LogOut, Menu, X, ExternalLink, Hammer, Upload, Download, Camera } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Logo, Loader, StatusBadge } from "@/components/Brand";
import { formatCurrency, formatDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Builder } from "@/components/admin/Builder";
import { AuthImage } from "@/components/AuthImage";
import { uploadToProject, fileDownloadUrl, humanSize } from "@/lib/files";

const NAV = [
  ["dashboard", "Dashboard", LayoutDashboard],
  ["clients", "Clients", Users],
  ["quotations", "Quotations", FileText],
  ["projects", "Projects", Hammer],
  ["builder", "Questionnaire Builder", Wrench],
  ["pricing", "Pricing", PoundSterling],
  ["preview", "Preview", Eye],
  ["settings", "Settings", SettingsIcon],
  ["audit", "Audit Log", ScrollText],
];
const STATUSES = ["Draft", "Estimate Generated", "Under Review", "Quotation Sent", "Accepted", "Declined", "Expired", "Converted to Project"];

export default function AdminDashboard() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [mobileNav, setMobileNav] = useState(false);

  const doLogout = async () => { await logout(); navigate("/"); };
  const NavItems = () => (
    <nav className="flex flex-col gap-1">
      {NAV.map(([key, label, Icon]) => (
        <button key={key} onClick={() => { setTab(key); setMobileNav(false); }}
          className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${tab === key ? "bg-amber-500/10 text-amber-400" : "text-slate-400 hover:bg-slate-800/60 hover:text-slate-200"}`}
          data-testid={`admin-nav-${key}`}>
          <Icon className="h-4 w-4" /> {label}
        </button>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-[#090A0C] text-slate-50 md:flex">
      <aside className="hidden w-64 shrink-0 flex-col justify-between border-r border-slate-800/80 bg-[#0F1117] p-6 md:flex">
        <div>
          <Logo />
          <p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-amber-500">Admin console</p>
          <div className="mt-8"><NavItems /></div>
        </div>
        <button onClick={doLogout} className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-400 hover:text-rose-400" data-testid="admin-logout-btn"><LogOut className="h-4 w-4" /> Sign out</button>
      </aside>

      <div className="flex items-center justify-between border-b border-slate-800/80 p-4 md:hidden">
        <Logo size="text-lg" />
        <button onClick={() => setMobileNav(!mobileNav)}>{mobileNav ? <X /> : <Menu />}</button>
      </div>
      {mobileNav && <div className="border-b border-slate-800 bg-[#0F1117] p-4 md:hidden"><NavItems /></div>}

      <main className="flex-1 px-4 py-8 sm:px-8">
        <div className="mx-auto max-w-6xl">
          {tab === "dashboard" && <AdminStats />}
          {tab === "clients" && <Clients />}
          {tab === "quotations" && <Quotations />}
          {tab === "projects" && <AdminProjects />}
          {tab === "builder" && <BuilderTab />}
          {tab === "pricing" && <Pricing />}
          {tab === "preview" && <Preview />}
          {tab === "settings" && <SettingsTab />}
          {tab === "audit" && <Audit />}
        </div>
      </main>
    </div>
  );
}

function AdminStats() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(({ data }) => setS(data)); }, []);
  if (!s) return <Loader />;
  const cards = [
    ["Total clients", s.total_clients], ["Quotations", s.total_quotations],
    ["Active quotations", s.active_quotations], ["Accepted", s.accepted_quotations],
    ["Projects", s.projects], ["Estimated value", formatCurrency(s.estimated_value)],
  ];
  return (
    <div className="evo-fade-up">
      <h1 className="font-heading text-3xl font-bold">Dashboard</h1>
      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-3">
        {cards.map(([l, v]) => (
          <div key={l} className="rounded-xl border border-slate-800/80 bg-[#151820] p-6" data-testid="admin-dashboard-stats-card">
            <p className="text-xs uppercase tracking-wide text-slate-500">{l}</p>
            <p className="mt-2 font-heading text-3xl font-bold text-amber-500">{v}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function Clients() {
  const [clients, setClients] = useState(null);
  const [detail, setDetail] = useState(null);
  useEffect(() => { api.get("/admin/clients").then(({ data }) => setClients(data.clients)); }, []);
  const open = async (c) => { const { data } = await api.get(`/admin/clients/${c.id}`); setDetail(data); };
  if (!clients) return <Loader />;
  return (
    <div className="evo-fade-up">
      <h1 className="font-heading text-3xl font-bold">Clients</h1>
      <div className="mt-6 overflow-x-auto rounded-2xl border border-slate-800 bg-[#151820]">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-800 text-xs uppercase text-slate-500">
            <tr><th className="p-4">Name</th><th className="p-4">Email</th><th className="p-4">Phone</th><th className="p-4">Quotes</th><th className="p-4">Joined</th></tr>
          </thead>
          <tbody>
            {clients.map((c) => (
              <tr key={c.id} onClick={() => open(c)} className="cursor-pointer border-b border-slate-800/50 hover:bg-slate-800/40" data-testid={`client-row-${c.id}`}>
                <td className="p-4 font-medium">{c.name}</td><td className="p-4 text-slate-400">{c.email}</td>
                <td className="p-4 text-slate-400">{c.phone || "—"}</td><td className="p-4">{c.quotation_count}</td>
                <td className="p-4 text-slate-400">{formatDate(c.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {detail && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm" onClick={() => setDetail(null)}>
          <div className="my-8 w-full max-w-lg rounded-2xl border border-slate-700 bg-[#151820] p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-xl font-bold">{detail.client.name}</h3>
            <p className="text-sm text-slate-400">{detail.client.email} · {detail.client.phone}</p>
            <p className="mt-1 text-sm text-slate-400">{[detail.client.address?.line1, detail.client.address?.city, detail.client.address?.postcode].filter(Boolean).join(", ")}</p>
            <h4 className="mt-5 text-sm font-semibold">Quotations</h4>
            <div className="mt-2 space-y-2">
              {detail.quotations.map((q) => (
                <div key={q.id} className="flex items-center justify-between rounded-lg border border-slate-800 p-3 text-sm">
                  <span>{q.project_type_name}</span><span className="font-mono text-amber-500">{formatCurrency(q.final_total)}</span>
                </div>
              ))}
              {detail.quotations.length === 0 && <p className="text-xs text-slate-500">No quotations.</p>}
            </div>
            <button onClick={() => setDetail(null)} className="mt-5 rounded-lg border border-slate-700 px-4 py-2 text-sm">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Quotations() {
  const [quotes, setQuotes] = useState(null);
  const [filter, setFilter] = useState({ status: "", type: "" });
  const [sel, setSel] = useState(null);
  const load = () => api.get("/admin/quotations").then(({ data }) => setQuotes(data.quotations));
  useEffect(() => { load(); }, []);
  if (!quotes) return <Loader />;
  const filtered = quotes.filter((q) => (!filter.status || q.status === filter.status) && (!filter.type || q.category_name === filter.type));
  const types = [...new Set(quotes.map((q) => q.category_name))];
  return (
    <div className="evo-fade-up">
      <h1 className="font-heading text-3xl font-bold">Quotations</h1>
      <div className="mt-4 flex flex-wrap gap-3">
        <select value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })} className="rounded-lg border border-slate-700 bg-[#0d0f13] px-3 py-2 text-sm" data-testid="filter-status">
          <option value="">All statuses</option>{STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
        <select value={filter.type} onChange={(e) => setFilter({ ...filter, type: e.target.value })} className="rounded-lg border border-slate-700 bg-[#0d0f13] px-3 py-2 text-sm" data-testid="filter-type">
          <option value="">All types</option>{types.map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>
      <div className="mt-4 overflow-x-auto rounded-2xl border border-slate-800 bg-[#151820]">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-800 text-xs uppercase text-slate-500">
            <tr><th className="p-4">Number</th><th className="p-4">Client</th><th className="p-4">Project</th><th className="p-4">Amount</th><th className="p-4">Date</th><th className="p-4">Status</th></tr>
          </thead>
          <tbody>
            {filtered.map((q) => (
              <tr key={q.id} onClick={() => setSel(q)} className="cursor-pointer border-b border-slate-800/50 hover:bg-slate-800/40" data-testid={`admin-quote-row-${q.id}`}>
                <td className="p-4 font-mono text-xs">{q.quotation_number}</td>
                <td className="p-4">{q.client?.name || "—"}</td>
                <td className="p-4 text-slate-400">{q.project_type_name}</td>
                <td className="p-4 font-mono text-amber-500">{formatCurrency(q.final_total)}</td>
                <td className="p-4 text-slate-400">{formatDate(q.created_at)}</td>
                <td className="p-4"><StatusBadge status={q.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sel && <QuoteAdminModal q={sel} onClose={() => { setSel(null); load(); }} />}
    </div>
  );
}

function QuoteAdminModal({ q, onClose }) {
  const [status, setStatus] = useState(q.status);
  const [adjAmount, setAdjAmount] = useState("");
  const [adjReason, setAdjReason] = useState("");
  const navigate = useNavigate();
  const inp = "w-full rounded-lg border border-slate-700 bg-[#0d0f13] px-3 py-2 text-sm outline-none focus:border-amber-500";

  const saveStatus = async () => { await api.patch(`/admin/quotations/${q.id}/status`, { status }); toast.success("Status updated & client emailed"); };
  const applyAdj = async () => {
    if (!adjAmount || !adjReason) return toast.error("Enter amount and reason");
    await api.patch(`/admin/quotations/${q.id}/adjust`, { amount: Number(adjAmount), reason: adjReason });
    toast.success("Adjustment applied"); onClose();
  };
  const convert = async () => { const { data } = await api.post(`/admin/quotations/${q.id}/convert`); toast.success("Converted to project"); onClose(); };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="my-8 w-full max-w-lg rounded-2xl border border-slate-700 bg-[#151820] p-6" onClick={(e) => e.stopPropagation()} data-testid="admin-quote-modal">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-heading text-xl font-bold">{q.quotation_number}</h3>
            <p className="text-sm text-slate-400">{q.project_type_name} · {q.client?.name}</p>
          </div>
          <button onClick={onClose} className="text-slate-400"><X /></button>
        </div>
        <div className="mt-4 rounded-lg border border-slate-800 bg-[#0d0f13] p-4">
          <div className="flex justify-between text-sm"><span className="text-slate-400">Calculated estimate</span><span className="font-mono">{formatCurrency(q.calculated_total)}</span></div>
          {q.admin_adjustment && <div className="mt-1 flex justify-between text-sm"><span className="text-amber-300">Adjustment</span><span className="font-mono text-amber-300">{formatCurrency(q.admin_adjustment.amount)}</span></div>}
          <div className="mt-2 flex justify-between border-t border-slate-800 pt-2 text-base font-bold"><span>Final quotation</span><span className="font-mono text-amber-500">{formatCurrency(q.final_total)}</span></div>
        </div>

        <div className="mt-4">
          <label className="mb-1 block text-xs text-slate-400">Status</label>
          <div className="flex gap-2">
            <select value={status} onChange={(e) => setStatus(e.target.value)} className={inp} data-testid="admin-status-select">{STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
            <button onClick={saveStatus} className="rounded-lg bg-amber-600 px-4 text-sm font-bold text-slate-950" data-testid="admin-save-status-btn">Update</button>
          </div>
        </div>

        <div className="mt-4">
          <label className="mb-1 block text-xs text-slate-400">Manual adjustment (£, use negative to reduce)</label>
          <div className="flex gap-2">
            <input type="number" value={adjAmount} onChange={(e) => setAdjAmount(e.target.value)} className={`${inp} w-32`} placeholder="-2500" data-testid="adj-amount-input" />
            <input value={adjReason} onChange={(e) => setAdjReason(e.target.value)} className={inp} placeholder="Reason" data-testid="adj-reason-input" />
            <button onClick={applyAdj} className="rounded-lg bg-slate-700 px-4 text-sm font-bold" data-testid="apply-adj-btn">Apply</button>
          </div>
        </div>

        <div className="mt-5 flex gap-2">
          <button onClick={() => navigate(`/quotation/${q.id}`)} className="flex-1 rounded-lg border border-slate-700 px-4 py-2.5 text-sm font-semibold">View / PDF</button>
          {q.status !== "Converted to Project" && <button onClick={convert} className="flex-1 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-bold text-slate-950" data-testid="convert-project-btn">Convert to Project</button>}
        </div>
      </div>
    </div>
  );
}

function useBuilderData() {
  const [state, setState] = useState(null);
  const load = () => Promise.all([
    api.get("/admin/categories"), api.get("/admin/project-types"), api.get("/admin/questions"),
  ]).then(([c, t, q]) => setState({ categories: c.data.categories, projectTypes: t.data.project_types, questions: q.data.questions }));
  useEffect(() => { load(); }, []);
  return [state, load];
}

function BuilderTab() {
  const [state, load] = useBuilderData();
  const [activeCat, setActiveCat] = useState(null);
  if (!state) return <Loader />;
  return (
    <div className="evo-fade-up">
      <h1 className="mb-6 font-heading text-3xl font-bold">Questionnaire Builder</h1>
      <Builder {...state} activeCat={activeCat || state.categories[0]?.id} setActiveCat={setActiveCat} reload={load} />
    </div>
  );
}

function Pricing() {
  const [types, setTypes] = useState(null);
  const [history, setHistory] = useState([]);
  const load = () => Promise.all([api.get("/admin/project-types"), api.get("/admin/price-history")])
    .then(([t, h]) => { setTypes(t.data.project_types); setHistory(h.data.history); });
  useEffect(() => { load(); }, []);
  if (!types) return <Loader />;
  const save = async (t, v) => { if (v !== t.base_price) { await api.put(`/admin/project-types/${t.id}`, { base_price: v }); toast.success("Price updated"); load(); } };
  return (
    <div className="evo-fade-up">
      <h1 className="font-heading text-3xl font-bold">Pricing</h1>
      <p className="mt-1 text-sm text-slate-400">Demo figures — editable by administrators. Not official EvoStruct pricing.</p>
      <div className="mt-6 space-y-2">
        {types.map((t) => (
          <div key={t.id} className="flex items-center justify-between rounded-lg border border-slate-800 bg-[#151820] px-4 py-3">
            <div><span className="font-medium">{t.name}</span> <span className="ml-2 text-xs text-slate-500">{t.category_id}</span></div>
            <div className="flex items-center gap-2"><span className="text-slate-500">£</span>
              <input type="number" defaultValue={t.base_price} onBlur={(e) => save(t, Number(e.target.value))} className="w-32 rounded-md border border-slate-700 bg-[#0d0f13] px-3 py-1.5 text-right font-mono text-sm outline-none focus:border-amber-500" data-testid={`pricing-input-${t.id}`} /></div>
          </div>
        ))}
      </div>
      <h2 className="mt-8 font-heading text-lg font-semibold">Price change history</h2>
      <div className="mt-3 overflow-x-auto rounded-2xl border border-slate-800 bg-[#151820]">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-800 text-xs uppercase text-slate-500"><tr><th className="p-3">Item</th><th className="p-3">Previous</th><th className="p-3">New</th><th className="p-3">By</th><th className="p-3">When</th></tr></thead>
          <tbody>
            {history.map((h) => (
              <tr key={h.id} className="border-b border-slate-800/50"><td className="p-3">{h.name}</td><td className="p-3 font-mono">{formatCurrency(h.previous_price)}</td><td className="p-3 font-mono text-amber-500">{formatCurrency(h.new_price)}</td><td className="p-3 text-slate-400">{h.changed_by}</td><td className="p-3 text-slate-400">{formatDate(h.at)}</td></tr>
            ))}
            {history.length === 0 && <tr><td className="p-4 text-slate-500" colSpan={5}>No price changes recorded yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Preview() {
  const [cats, setCats] = useState([]);
  useEffect(() => { api.get("/admin/categories").then(({ data }) => setCats(data.categories)); }, []);
  return (
    <div className="evo-fade-up">
      <h1 className="font-heading text-3xl font-bold">Preview Questionnaire</h1>
      <p className="mt-1 text-sm text-slate-400">Test the client experience and see the calculated quotation. Nothing is saved.</p>
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
        {cats.map((c) => (
          <a key={c.id} href={`/quote/${c.id}?preview=1`} target="_blank" rel="noreferrer"
            className="flex items-center justify-between rounded-xl border border-slate-800 bg-[#151820] p-5 hover:border-amber-500/40" data-testid={`preview-${c.id}`}>
            <span className="font-heading text-lg font-semibold">{c.name}</span>
            <ExternalLink className="h-4 w-4 text-amber-400" />
          </a>
        ))}
      </div>
    </div>
  );
}

function SettingsTab() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/settings").then(({ data }) => setS(data.settings)); }, []);
  if (!s) return <Loader />;
  const inp = "w-full rounded-lg border border-slate-700 bg-[#0d0f13] px-3 py-2 text-sm outline-none focus:border-amber-500";
  const save = async () => {
    await api.put("/admin/settings", { show_live_estimate: s.show_live_estimate, disclaimer: s.disclaimer, quote_validity_days: Number(s.quote_validity_days), company: s.company });
    toast.success("Settings saved");
  };
  return (
    <div className="evo-fade-up max-w-2xl">
      <h1 className="font-heading text-3xl font-bold">Settings</h1>
      <div className="mt-6 space-y-5 rounded-2xl border border-slate-800 bg-[#151820] p-6">
        <label className="flex items-center justify-between">
          <span className="text-sm font-medium">Show live estimate during questionnaire</span>
          <button onClick={() => setS({ ...s, show_live_estimate: !s.show_live_estimate })} className={`h-6 w-11 rounded-full transition-colors ${s.show_live_estimate ? "bg-amber-500" : "bg-slate-700"}`} data-testid="toggle-live-estimate">
            <span className={`block h-5 w-5 rounded-full bg-white transition-transform ${s.show_live_estimate ? "translate-x-5" : "translate-x-0.5"}`} />
          </button>
        </label>
        <div><label className="mb-1 block text-xs text-slate-400">Disclaimer</label>
          <textarea rows={5} value={s.disclaimer} onChange={(e) => setS({ ...s, disclaimer: e.target.value })} className={inp} data-testid="settings-disclaimer" /></div>
        <div><label className="mb-1 block text-xs text-slate-400">Quote validity (days)</label>
          <input type="number" value={s.quote_validity_days} onChange={(e) => setS({ ...s, quote_validity_days: e.target.value })} className={`${inp} w-32`} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="mb-1 block text-xs text-slate-400">Company name</label><input value={s.company?.name || ""} onChange={(e) => setS({ ...s, company: { ...s.company, name: e.target.value } })} className={inp} /></div>
          <div><label className="mb-1 block text-xs text-slate-400">Phone</label><input value={s.company?.phone || ""} onChange={(e) => setS({ ...s, company: { ...s.company, phone: e.target.value } })} className={inp} /></div>
        </div>
        <button onClick={save} className="rounded-xl bg-amber-600 px-6 py-3 font-bold text-slate-950 hover:bg-amber-500" data-testid="settings-save-btn">Save settings</button>
      </div>
    </div>
  );
}

function AdminProjects() {
  const [projects, setProjects] = useState(null);
  const [sel, setSel] = useState(null);
  const load = () => api.get("/admin/projects").then(({ data }) => setProjects(data.projects));
  useEffect(() => { load(); }, []);
  if (!projects) return <Loader />;
  return (
    <div className="evo-fade-up">
      <h1 className="font-heading text-3xl font-bold">Projects</h1>
      <p className="mt-1 text-sm text-slate-400">Convert an accepted quotation to a project from the Quotations tab.</p>
      {projects.length === 0 ? (
        <p className="mt-6 text-sm text-slate-500">No projects yet.</p>
      ) : (
        <div className="mt-6 space-y-3">
          {projects.map((p) => (
            <button key={p.id} onClick={() => setSel(p.id)} className="flex w-full flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-800 bg-[#151820] p-4 text-left hover:border-amber-500/40" data-testid={`admin-project-row-${p.id}`}>
              <div>
                <p className="font-semibold">{p.name}</p>
                <p className="text-xs text-slate-500">{p.client?.name} · {formatCurrency(p.value)}</p>
              </div>
              <StatusBadge status={p.status} />
            </button>
          ))}
        </div>
      )}
      {sel && <AdminProjectModal projectId={sel} onClose={() => { setSel(null); load(); }} />}
    </div>
  );
}

function AdminProjectModal({ projectId, onClose }) {
  const [bundle, setBundle] = useState(null);
  const [stage, setStage] = useState("");
  const [update, setUpdate] = useState({ title: "", body: "" });
  const [busy, setBusy] = useState(false);
  const photoRef = useRef();
  const docRef = useRef();
  const load = () => api.get(`/admin/projects/${projectId}`).then(({ data }) => { setBundle(data); setStage(data.project.status); });
  useEffect(() => { load(); }, [projectId]);

  const saveStage = async () => { await api.patch(`/admin/projects/${projectId}/stage`, { status: stage }); toast.success("Stage updated"); load(); };
  const postUpdate = async () => {
    if (!update.title) return toast.error("Enter a title");
    await api.post(`/admin/projects/${projectId}/updates`, update);
    toast.success("Update posted"); setUpdate({ title: "", body: "" }); load();
  };
  const upload = async (e, kind) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try { await uploadToProject({ projectId, admin: true, kind, file }); toast.success("Uploaded"); load(); }
    catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };

  if (!bundle) return null;
  const { project, documents, photos, updates } = bundle;
  const inp = "w-full rounded-lg border border-slate-700 bg-[#0d0f13] px-3 py-2 text-sm outline-none focus:border-amber-500";
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="my-8 w-full max-w-2xl rounded-2xl border border-slate-700 bg-[#151820] p-6" onClick={(e) => e.stopPropagation()} data-testid="admin-project-modal">
        <div className="flex items-center justify-between">
          <div><h3 className="font-heading text-xl font-bold">{project.name}</h3><p className="text-sm text-slate-400">{bundle.client?.name} · {bundle.client?.email}</p></div>
          <button onClick={onClose} className="text-slate-400"><X /></button>
        </div>

        <div className="mt-4">
          <label className="mb-1 block text-xs text-slate-400">Current stage</label>
          <div className="flex gap-2">
            <select value={stage} onChange={(e) => setStage(e.target.value)} className={inp} data-testid="project-stage-select">
              {(project.stages || []).map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
            </select>
            <button onClick={saveStage} className="rounded-lg bg-amber-600 px-4 text-sm font-bold text-slate-950" data-testid="save-stage-btn">Set</button>
          </div>
        </div>

        <div className="mt-4 flex gap-3">
          <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e, "photo")} data-testid="admin-photo-input" />
          <button onClick={() => photoRef.current?.click()} disabled={busy} className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-slate-700 py-2.5 text-sm font-semibold hover:border-amber-500/50" data-testid="admin-upload-photo-btn"><Camera className="h-4 w-4" /> Add site photo</button>
          <input ref={docRef} type="file" className="hidden" onChange={(e) => upload(e, "document")} data-testid="admin-doc-input" />
          <button onClick={() => docRef.current?.click()} disabled={busy} className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-slate-700 py-2.5 text-sm font-semibold hover:border-amber-500/50" data-testid="admin-upload-doc-btn"><Upload className="h-4 w-4" /> Add document</button>
        </div>

        <div className="mt-4 rounded-lg border border-slate-800 p-3">
          <span className="text-sm font-semibold">Post a site update</span>
          <input value={update.title} onChange={(e) => setUpdate({ ...update, title: e.target.value })} className={`${inp} mt-2`} placeholder="Title (e.g. Steel beams installed)" data-testid="update-title-input" />
          <textarea rows={2} value={update.body} onChange={(e) => setUpdate({ ...update, body: e.target.value })} className={`${inp} mt-2`} placeholder="Details" data-testid="update-body-input" />
          <button onClick={postUpdate} className="mt-2 rounded-lg bg-slate-700 px-4 py-2 text-sm font-bold" data-testid="post-update-btn">Post update</button>
        </div>

        {photos.length > 0 && (
          <div className="mt-4">
            <p className="mb-2 text-sm font-semibold">Photos ({photos.length})</p>
            <div className="grid grid-cols-4 gap-2">
              {photos.map((p) => <AuthImage key={p.id} fileId={p.id} className="aspect-square w-full rounded-lg object-cover" />)}
            </div>
          </div>
        )}
        {documents.length > 0 && (
          <div className="mt-4">
            <p className="mb-2 text-sm font-semibold">Documents ({documents.length})</p>
            <div className="space-y-2">
              {documents.map((d) => (
                <div key={d.id} className="flex items-center justify-between rounded-lg border border-slate-800 px-3 py-2 text-sm">
                  <span className="truncate">{d.original_filename} <span className="text-xs text-slate-500">({humanSize(d.size)})</span></span>
                  <a href={fileDownloadUrl(d.id)} target="_blank" rel="noreferrer" className="text-amber-400"><Download className="h-4 w-4" /></a>
                </div>
              ))}
            </div>
          </div>
        )}
        {updates.length > 0 && (
          <div className="mt-4">
            <p className="mb-2 text-sm font-semibold">Recent updates</p>
            <div className="space-y-2">
              {updates.map((u) => <div key={u.id} className="rounded-lg border border-slate-800 px-3 py-2 text-sm"><span className="font-medium">{u.title}</span> <span className="text-xs text-slate-500">· {formatDate(u.created_at)}</span></div>)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Audit() {
  const [logs, setLogs] = useState(null);
  useEffect(() => { api.get("/admin/audit-logs").then(({ data }) => setLogs(data.logs)); }, []);
  if (!logs) return <Loader />;
  return (
    <div className="evo-fade-up">
      <h1 className="font-heading text-3xl font-bold">Audit Log</h1>
      <div className="mt-6 overflow-x-auto rounded-2xl border border-slate-800 bg-[#151820]">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-800 text-xs uppercase text-slate-500"><tr><th className="p-3">Action</th><th className="p-3">Entity</th><th className="p-3">User</th><th className="p-3">When</th></tr></thead>
          <tbody>
            {logs.map((l) => (
              <tr key={l.id} className="border-b border-slate-800/50"><td className="p-3 font-mono text-xs text-amber-400">{l.action}</td><td className="p-3 text-slate-400">{l.entity}</td><td className="p-3 text-slate-400">{l.user_email}</td><td className="p-3 text-slate-400">{formatDate(l.at)}</td></tr>
            ))}
            {logs.length === 0 && <tr><td className="p-4 text-slate-500" colSpan={4}>No activity yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
